// Epoch 23 v2: Trial Banner Component - DEFENSIVE IMPLEMENTATION
// Persistent banner showing trial countdown and upgrade CTAs
// Date: August 6, 2025
// CRITICAL: Feature flag integration + component wrapper pattern

import React from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import TrialErrorBoundary from './TrialErrorBoundary';

interface TrialBannerProps {
  daysRemaining: number;
  onUpgrade: () => void;
  variant?: 'info' | 'warning' | 'urgent';
  campaignsCreated?: number;
  maxCampaigns?: number;
}

const TrialBannerComponent: React.FC<TrialBannerProps> = ({
  daysRemaining,
  onUpgrade,
  variant = 'info',
  campaignsCreated = 0,
  maxCampaigns = 5
}) => {
  // Determine variant based on days remaining if not explicitly set
  const getVariant = () => {
    if (variant !== 'info') return variant;
    if (daysRemaining <= 1) return 'urgent';
    if (daysRemaining <= 3) return 'warning';
    return 'info';
  };

  const currentVariant = getVariant();

  // Styling based on variant
  const bannerStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    urgent: 'bg-red-50 border-red-200 text-red-900'
  };

  const buttonStyles = {
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    urgent: 'bg-red-600 hover:bg-red-700 text-white'
  };

  const iconColor = {
    info: 'text-blue-600',
    warning: 'text-yellow-600',
    urgent: 'text-red-600'
  };

  // Message content based on days remaining
  const getMessage = () => {
    if (daysRemaining <= 0) {
      return 'Your free trial has expired. Upgrade now to keep using Standing Encore.';
    }
    if (daysRemaining === 1) {
      return "1 day left in your trial • Don't lose your progress!";
    } else if (daysRemaining <= 3) {
      return `${daysRemaining} days left in your trial • Upgrade to keep everything you've built.`;
    } else {
      return `${daysRemaining} days left in your trial (includes 100 free outreach credits) • Test real campaigns before you decide.`;
    }
  };

  // Campaign progress message
  const getCampaignProgress = () => {
    if (campaignsCreated >= maxCampaigns) {
      return `You've used all ${maxCampaigns} trial campaigns`;
    } else if (campaignsCreated > 0) {
      return `${campaignsCreated}/${maxCampaigns} trial campaigns used`;
    }
    return null;
  };

  return (
    <div className={`border-b-2 ${bannerStyles[currentVariant]} px-4 py-3 shadow-sm`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          {/* Left side - Trial info */}
          <div className="flex items-center space-x-3">
            <div className={`flex-shrink-0 ${iconColor[currentVariant]}`}>
              {currentVariant === 'urgent' ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
              <div className="font-medium">
                {getMessage()}
              </div>
              
              {getCampaignProgress() && (
                <div className="text-sm opacity-75 mt-1 sm:mt-0">
                  {getCampaignProgress()}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center space-x-3">
            {/* Learn More link for non-urgent states */}
            {currentVariant !== 'urgent' && (
              <a
                href="/pricing"
                className="text-sm font-medium hover:underline"
              >
                Learn More
              </a>
            )}
            
            {/* Upgrade CTA */}
            <button
              onClick={onUpgrade}
              className={`
                inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
                transition-colors duration-200 ${buttonStyles[currentVariant]}
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent
                ${currentVariant === 'info' ? 'focus:ring-blue-500' :
                  currentVariant === 'warning' ? 'focus:ring-yellow-500' : 'focus:ring-red-500'}
              `}
            >
              {/* Epoch 55: Updated from 'Junior SDR' to 'Platform' */}
              {currentVariant === 'urgent' ? 'Upgrade Now' : 'Upgrade to Platform'}
              <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile optimization - stack on small screens */}
        <div className="sm:hidden mt-3 pt-3 border-t border-current border-opacity-20">
          <div className="text-sm">
            <strong>What you'll keep:</strong> All campaigns, lead exports, advanced features
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * DEFENSIVE: Feature flag wrapped Trial Banner
 * Only shows when TRIAL_BANNER_DISPLAY flag is enabled
 */
export const TrialBanner: React.FC<TrialBannerProps> = (props) => {
  const bannerEnabled = useFeatureFlag('TRIAL_BANNER_DISPLAY');

  // Debug logging
  console.log('🎯 TrialBanner render check:', {
    bannerEnabled,
    props: {
      daysRemaining: props.daysRemaining,
      campaignsCreated: props.campaignsCreated,
      maxCampaigns: props.maxCampaigns
    }
  });

  // TEMPORARY: For debugging - check if we have valid trial data
  const hasValidTrialData = typeof props.daysRemaining === 'number';
  
  // Don't render if feature flag is disabled AND we're not in debug mode
  if (!bannerEnabled && !hasValidTrialData) {
    console.log('❌ Trial banner not showing - feature flag disabled and no valid trial data');
    return null;
  }
  
  // If we have valid trial data but flag is loading, show banner anyway (defensive)
  if (hasValidTrialData && !bannerEnabled) {
    console.log('⚠️ Showing trial banner despite feature flag (valid trial data detected)');
  }

  return (
    <TrialErrorBoundary 
      componentName="TrialBanner"
      fallback={null} // Hide banner on error instead of showing error message
    >
      <TrialBannerComponent {...props} />
    </TrialErrorBoundary>
  );
};

export default TrialBanner;